import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { PRODUCTS } from "../data/products";
import { CartItem, FreeShippingBar, CrossSell, CartSummary } from "./cart/CartParts";

const inputCls = "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#C85A32] transition-colors";

export const CartDrawer = () => {
  const { t, lang, cartOpen, setCartOpen, totals, updateQty, removeItem, promo, applyPromoCode, addToCart, stock, config, track } = useStore();
  const navigate = useNavigate();
  const tc = t.cartDrawer;
  const [promoInput, setPromoInput] = useState("");

  const close = () => setCartOpen(false);
  const inCart = new Set(totals.items.map((i) => i.id));
  const suggestions = PRODUCTS.filter((p) => !inCart.has(p.id) && stock?.[p.id] !== 0 && p.id !== "duo-rituel").slice(0, 2);

  const applyPromo = async () => {
    if (!config.promo_enabled || !promoInput.trim()) return toast.error(tc.promoBad);
    try {
      const c = await applyPromoCode(promoInput, totals.subtotal);
      toast.success(`${c.code} : −${c.type === "percent" ? `${c.value}%` : `${c.value.toFixed(2)} CHF`}${c.free_shipping ? " · livraison offerte" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || tc.promoBad); }
  };

  const startPayment = () => { track("checkout_start"); close(); navigate("/checkout"); };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} onClick={close} className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-testid="cart-overlay" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-[#FAF7F2] flex flex-col shadow-2xl" data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
              <h2 className="font-serif text-2xl text-stone-900">{tc.title}</h2>
              <button data-testid="cart-close-button" onClick={close} className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center hover:bg-stone-900 hover:text-white transition-colors duration-300"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5" data-lenis-prevent>
              {totals.items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-2" data-testid="cart-empty-state">
                  <p className="font-serif text-xl text-stone-800 italic">{tc.empty}</p>
                  <p className="text-sm text-stone-500">{tc.emptySub}</p>
                  <button data-testid="cart-continue-shopping" onClick={close} className="mt-4 rounded-full border border-stone-900 px-6 py-2.5 text-[11px] uppercase tracking-[0.15em] hover:bg-stone-900 hover:text-white transition-colors">{tc.continueShopping}</button>
                </div>
              ) : (
                <>
                  <FreeShippingBar totals={totals} config={config} tc={tc} />
                  <ul className="space-y-5">
                    {totals.items.map((item) => <CartItem key={item.id} item={item} lang={lang} updateQty={updateQty} removeItem={removeItem} />)}
                  </ul>
                  <div className="mt-8 flex gap-2">
                    <input data-testid="promo-code-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder={tc.promo} className={`${inputCls} flex-1`} />
                    <button data-testid="promo-apply-button" onClick={applyPromo} className="rounded-xl border border-stone-900 px-4 text-[11px] uppercase tracking-[0.15em] hover:bg-stone-900 hover:text-white transition-colors duration-300">{tc.promoApply}</button>
                  </div>
                  <CrossSell suggestions={suggestions} lang={lang} t={t} addToCart={addToCart} />
                </>
              )}
            </div>
            {totals.items.length > 0 && <CartSummary totals={totals} promo={promo} tc={tc} trust={t.trust} onCheckout={startPayment} submitting={false} />}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
